import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faPlus, 
  faTrash, 
  faEdit, 
  faCheck, 
  faTimes,
  faExclamationTriangle,
  faInfoCircle,
  faToggleOn,
  faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import { promoService } from '../../../services/promoService';

export default function ManagePromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', data: '', mimeType: '', isActive: true });
  const [previewUrl, setPreviewUrl] = useState('');
  const [editingPromo, setEditingPromo] = useState(null);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const list = await promoService.list();
      setPromos(list);
    } catch (e) {
      setError(e.message || 'Failed to load promos');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  function resetForm() {
    setForm({ title: '', description: '', data: '', mimeType: '', isActive: true });
    setPreviewUrl('');
    setEditingPromo(null);
  }

  function handleFileChange(file) {
    if (!file) {
      setForm(prev => ({ ...prev, data: '', mimeType: '' }));
      setPreviewUrl('');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      setForm(prev => ({ ...prev, data: dataUrl, mimeType: file.type || '' }));
      setPreviewUrl(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      if (!form.data) throw new Error('Please select an image');
      if (editingPromo) {
        await promoService.update(editingPromo._id || editingPromo.id, {
          title: form.title,
          description: form.description,
          data: form.data,
          mimeType: form.mimeType,
          isActive: form.isActive,
        });
      } else {
        await promoService.create(form);
      }
      await refresh();
      setShowModal(false);
      resetForm();
    } catch (e) {
      setError(e.message || 'Failed to save promo');
    }
  }

  async function handleDelete(item) {
    if (!window.confirm('Delete this promo?')) return;
    try {
      await promoService.remove(item._id || item.id);
      await refresh();
    } catch (e) {
      setError(e.message || 'Failed to delete promo');
    }
  }

  async function toggleActive(item) {
    try {
      await promoService.update(item._id || item.id, { isActive: !item.isActive });
      await refresh();
    } catch (e) {
      setError(e.message || 'Failed to update promo');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Promos</h1>
              <p className="mt-1 text-sm text-gray-600">Create and manage promotional banners</p>
            </div>
            <button
              onClick={() => { setShowModal(true); resetForm(); }}
              className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 flex items-center transition-colors transform transition-transform duration-300 hover:scale-105 active:scale-95"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              New Promo
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 animate-fade-in">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 text-red-500" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-700 font-medium">Loading promos...</p>
            </div>
          </div>
        ) : promos.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faImage} className="text-gray-400 text-2xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No promos yet</h3>
            <p className="text-gray-500 mb-6">Create your first promotional banner to get started</p>
            <button
              onClick={() => { setShowModal(true); resetForm(); }}
              className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 flex items-center mx-auto transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Create Promo
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {promos.map((p) => (
              <div key={p._id || p.id} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-lg">
                <div className="h-56 bg-gray-100 relative overflow-hidden">
                  {(p.mimeType || '').startsWith('image/') ? (
                    <img src={p.data} alt={p.title || 'promo'} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <FontAwesomeIcon icon={faImage} className="text-3xl" />
                    </div>
                  )}
                  <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium flex items-center ${
                    p.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    <FontAwesomeIcon icon={p.isActive ? faCheck : faTimes} className="mr-1 text-xs" />
                    {p.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
                <div className="p-5">
                  <div className="mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">{p.title || 'Promo'}</h3>
                    {p.description && <p className="text-gray-600 text-sm mt-1">{p.description}</p>}
                  </div>
                  <div className="flex justify-between">
                    <button
                      onClick={() => toggleActive(p)}
                      className={`px-3 py-2 rounded-xl flex items-center transition-colors ${
                        p.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title={p.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <FontAwesomeIcon icon={p.isActive ? faToggleOn : faToggleOff} className="mr-1" />
                      {p.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => { 
                          setEditingPromo(p); 
                          setShowModal(true); 
                          setForm({ 
                            title: p.title || '', 
                            description: p.description || '', 
                            data: p.data || '', 
                            mimeType: p.mimeType || '', 
                            isActive: !!p.isActive 
                          }); 
                          setPreviewUrl(p.data || ''); 
                        }}
                        className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center justify-center transition-colors"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        className="w-10 h-10 rounded-xl bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center transition-colors"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => { setShowModal(false); resetForm(); }}></div>
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col transform transition-transform duration-300 scale-95 animate-scale-in">
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">{editingPromo ? 'Edit Promo' : 'New Promo'}</h3>
                    <button 
                      onClick={() => { setShowModal(false); resetForm(); }} 
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input 
                      type="text" 
                      value={form.title} 
                      onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors" 
                      placeholder="Enter promo title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea 
                      value={form.description} 
                      onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors" 
                      rows="3"
                      placeholder="Enter promo description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <FontAwesomeIcon icon={faImage} className="w-8 h-8 mb-2 text-gray-400" />
                          <p className="text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                        </div>
                        <input 
                          type="file" 
                          accept="image/*,.pdf" 
                          onChange={e => handleFileChange(e.target.files && e.target.files[0])} 
                          className="hidden" 
                        />
                      </label>
                    </div>
                    {previewUrl && (
                      <div className="mt-4 border rounded-xl bg-gray-50 overflow-hidden">
                        {(form.mimeType || '').startsWith('image/') ? (
                          <div
                            className="w-full h-64 bg-center bg-cover rounded-xl"
                            style={{ backgroundImage: `url(${previewUrl})` }}
                          />
                        ) : (
                          <div className="w-full h-32 flex items-center justify-center text-gray-400 border rounded-xl">
                            Preview not available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center">
                    <input 
                      id="isActive" 
                      type="checkbox" 
                      checked={form.isActive} 
                      onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))} 
                      className="w-4 h-4 text-red-600 rounded focus:ring-red-500" 
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Active</label>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button 
                      type="button" 
                      onClick={() => { setShowModal(false); resetForm(); }} 
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors transform transition-transform duration-300 hover:scale-105 active:scale-95"
                    >
                      {editingPromo ? 'Update Promo' : 'Create Promo'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}